<script setup lang="ts">
/**
 * Form drawer for create + edit. Container owns mutation lifecycle.
 * The drawer renders TreasuryAccountForm and forwards the submit
 * event upward.
 */
import Box from '@uiKit/elements/Box.vue'
import AppDrawer from '@uiKit/elements/Drawer.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import TreasuryAccountForm, {
  type TreasuryAccountFormMode,
  type TreasuryAccountFormValues,
} from './TreasuryAccountForm.vue'

interface Props {
  visible: boolean
  mode: TreasuryAccountFormMode
  initialValues?: Partial<TreasuryAccountFormValues>
  loading: boolean
  apiError: string | null
}

withDefaults(defineProps<Props>(), {
  initialValues: () => ({}),
  apiError: null,
})

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'submit', values: TreasuryAccountFormValues): void
}>()

function handleClose() {
  emit('update:visible', false)
}

function handleSubmit(values: TreasuryAccountFormValues) {
  emit('submit', values)
}
</script>

<template>
  <AppDrawer
    :model-value="visible"
    position="right"
    width="540px"
    :show-close-icon="false"
    :dismissable="!loading"
    @update:model-value="(v) => !v && handleClose()"
  >
    <template #header>
      <DrawerHeader
        :title="mode === 'create' ? 'Add treasury account' : 'Edit treasury account'"
        :subtitle="
          mode === 'create' ? 'Create a new settlement bank account.' : 'Update mutable fields.'
        "
        :disable-close="loading"
        @close="handleClose"
      />
    </template>

    <Box>
      <TreasuryAccountForm
        :mode="mode"
        :initial-values="initialValues"
        :loading="loading"
        :api-error="apiError"
        @submit="handleSubmit"
        @cancel="handleClose"
      />
    </Box>
  </AppDrawer>
</template>
